/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Cartesian3,
  Cartesian2,
  Color,
  Material,
  Viewer as CesiumViewer,
  OpenStreetMapImageryProvider,
  Ion,
  NearFarScalar,
  ScreenSpaceEventType,
  Scene,
  defined,
  PolylineMaterialAppearance,
} from "cesium";
import {
  CameraFlyTo,
  Viewer,
  CesiumComponentRef,
  Entity,
  PolylineCollection,
  PointGraphics,
  PolylineGraphics,
  Polyline,
  ScreenSpaceCameraController,
  LabelGraphics,
  ImageryLayer,
  RectangleGraphics,
  ScreenSpaceEventHandler,
  GroundPolylinePrimitive,
} from "resium";
import SceneMode from "cesium/Source/Scene/SceneMode";
import GroundPolylineGeometry from "cesium/Source/Core/GroundPolylineGeometry";
import GeometryInstance from "cesium/Source/Core/GeometryInstance";
import Spinner from "../DesignSystem/spinner";
import { CESIUM_API_TOKEN } from "../../utils/apiConstants";
import { MAP_ZOOM, MAP_COLOR } from "../../utils/appConstants";

Ion.defaultAccessToken = CESIUM_API_TOKEN;

interface MapViewProps {
  onBandwidthClick: (neId: string, feId: string, details?: any) => void;
  onNEClick: (neId: string) => void;
  networkElements: any[];
  geographicCenter: { latitude: number; longitude: number; zoomFactor: number };
  bandwidthPorts: any[];
  nodeIds: any[];
  myNetwork?: boolean;
}


const display: "ground" | "poly" = "ground";

const MapView: React.FC<MapViewProps> = ({ onBandwidthClick, networkElements, geographicCenter, bandwidthPorts, nodeIds, myNetwork, onNEClick }) => {
  const handleBandwidthClick = useCallback(
    (neId: string, feId: string, details?: any) => {
      onBandwidthClick(neId, feId, details);
    },
    [onBandwidthClick]
  );
  const handleNEClick = useCallback(
    (e: any, neId: string) => {
      onNEClick(neId);
    },
    [onNEClick]
  );
  const [mapLoading, setMapLoading] = useState<boolean>(false);
  const [hoveredNEs, setHoveredNEs] = useState<{ fe: number; ne: number }>({ ne: 0, fe: 0 });
  const getEntities = (): any => {
    return networkElements.map((ne: any, index: number) => {
      const outlineColor = myNetwork ? "#000000" : "#64AF29";
      const fillColor = ne.on_net && myNetwork ? MAP_COLOR.GREEN : "#ffffff";
      if (isNaN(parseFloat(ne.latitude)) || isNaN(parseFloat(ne.longitude))) return;
      return (
        <Entity
          key={index}
          id={ne.id}
          position={Cartesian3.fromDegrees(parseFloat(ne.longitude), parseFloat(ne.latitude))}
          name={ne.ne_name}
          onClick={(e: any) => handleNEClick(e, ne.id)}
          onMouseEnter={() => setHoveredNEs({ ne: ne.id, fe: ne.id })}
        >
          <PointGraphics
            pixelSize={ne.on_net ? 20 : 10}
            outlineColor={Color.fromCssColorString(outlineColor)}
            color={Color.fromCssColorString(fillColor)}
            outlineWidth={4}
          />
          <LabelGraphics
            show={Number(hoveredNEs.ne) === Number(ne.id) || Number(hoveredNEs.fe) === Number(ne.id)}
            text={ne.ne_name}
            showBackground
            scale={1}
            font="Helvetica Neue Regular"
            backgroundPadding={new Cartesian2(4, 4)}
            backgroundColor={ne.on_net ? Color.fromCssColorString(MAP_COLOR.GREEN) : Color.fromCssColorString("#000000")}
            verticalOrigin={-1}
            eyeOffset={new Cartesian3(0, 0, -100)}
          />
        </Entity>
      );
    });
  };
  const [entities, setEntities] = useState<any>("");

  useEffect(() => {
    const ent = getEntities();
    setEntities(ent);
  }, [networkElements, hoveredNEs]);

  const getColorFromCCStatus = (color: string) => {
    if (color === "green") {
      return MAP_COLOR.GREEN;
    }
    if (color === "yellow") {
      return MAP_COLOR.YELLOW;
    }
    if (color === "white") {
      return MAP_COLOR.WHITE;
    }
    if (color === "grey") {
      return MAP_COLOR.GREY;
    }
    if (color === "green-fill") {
      return MAP_COLOR.LIGHT_GREEN;
    }
    return MAP_COLOR.BLACK;
  };

  const getColorPrecedence = (color_one: string, color_two: string) => {
    if (color_one === MAP_COLOR.GREEN || color_two === MAP_COLOR.GREEN) {
      return MAP_COLOR.GREEN;
    }
    if (color_one === MAP_COLOR.LIGHT_GREEN || color_two === MAP_COLOR.LIGHT_GREEN) {
      return MAP_COLOR.LIGHT_GREEN;
    }
    if (color_one === MAP_COLOR.YELLOW || color_two === MAP_COLOR.YELLOW) {
      return MAP_COLOR.YELLOW;
    }
    if (color_one === MAP_COLOR.WHITE || color_two === MAP_COLOR.WHITE) {
      return MAP_COLOR.WHITE;
    }
    if (color_one === MAP_COLOR.GREY || color_two === MAP_COLOR.GREY) {
      return MAP_COLOR.GREY;
    }
    return MAP_COLOR.BLACK;
  };

  const getNEString = (ne_one: string, ne_two: string): string => {
    return `${Math.min(Number(ne_one), Number(ne_two)).toString()}-${Math.max(Number(ne_one), Number(ne_two)).toString()}`;
  };

  const splitNEString = (ne: string): [string, string] => {
    const split = ne.split("-");
    return [split[0], split[1]];
  };

  const getBDGroups = useCallback(() => {
    const bd = [...bandwidthPorts];
    const ne_map = new Map<string, { color: string; item: any[] }>();
    bd.forEach((bandwidth: any) => {
      const ne: string = getNEString(bandwidth.nne_id, bandwidth.fne_id);
      if (!ne_map.has(ne)) {
        ne_map.set(ne, {
          color: getColorFromCCStatus(bandwidth.color),
          item: [bandwidth],
        });
      } else {
        const old_map: { color: string; item: any[] } | undefined = ne_map.get(ne);
        if (old_map) {
          const old_color = old_map.color;
          const old_item = [...old_map.item];
          old_item.push(bandwidth);
          ne_map.delete(ne);
          ne_map.set(ne, {
            color: getColorPrecedence(old_color, getColorFromCCStatus(bandwidth.color)),
            item: old_item,
          });
        }
      }
    });
    return Array.from(ne_map, ([ne, details]) => ({ ne, details }));
  }, [bandwidthPorts]);

  const bandwidthLines = useMemo(() => {
    if (myNetwork) {
      if (bandwidthPorts) {
        const bdGroups = getBDGroups();
        return bdGroups.map((item: any, index: number) => {
          const thisNE = splitNEString(item.ne);
          const nearEndNE = networkElements.find((ne) => ne.id == thisNE[0]);
          const farEndNE = networkElements.find((ne) => ne.id == thisNE[1]);
          if (
            isNaN(parseFloat(nearEndNE.longitude)) ||
            isNaN(parseFloat(nearEndNE.latitude)) ||
            isNaN(parseFloat(farEndNE.longitude)) ||
            isNaN(parseFloat(farEndNE.latitude))
          )
            return;
          const positions = Cartesian3.fromDegreesArray([
            parseFloat(nearEndNE.longitude),
            parseFloat(nearEndNE.latitude),
            parseFloat(farEndNE.longitude),
            parseFloat(farEndNE.latitude),
          ]);
          const { color } = item.details;
          const isDoubleLine = color === MAP_COLOR.WHITE || color === MAP_COLOR.GREEN;
          const width = isDoubleLine ? 7 : 4;
          const material2 = isDoubleLine
            ? Material.fromType("PolylineOutline", {
                color: Color.fromCssColorString(MAP_COLOR.WHITE),
                outlineColor: Color.fromCssColorString(color === MAP_COLOR.GREEN ? MAP_COLOR.GREEN : MAP_COLOR.BLACK),
                outlineWidth: 4,
              })
            : Material.fromType("Color", { color: Color.fromCssColorString(color) });
          const ground = (
            <GroundPolylinePrimitive
              key={index}
              onClick={() => handleBandwidthClick(nearEndNE.id, farEndNE.id, item.details.item)}
              appearance={new PolylineMaterialAppearance({ material: material2 })}
              geometryInstances={new GeometryInstance({ id: `${nearEndNE.id}-${farEndNE.id}`, geometry: new GroundPolylineGeometry({ width, positions }) })}
              onMouseEnter={() => setHoveredNEs({ ne: nearEndNE.id, fe: farEndNE.id })}
              // onMouseLeave={() => setHoveredNEs({ ne: 0, fe: 0 })}
            />
          );
          return ground;
        });
      }
      return "";
    }
    return bandwidthPorts.map((item: any, index: number) => {
      const nearEndNE = networkElements.find((ne) => ne.id === item.nne_id);
      const farEndNE = networkElements.find((ne) => ne.id === item.fne_id);
      const positions = Cartesian3.fromDegreesArray([
        parseFloat(nearEndNE.longitude),
        parseFloat(nearEndNE.latitude),
        parseFloat(farEndNE.longitude),
        parseFloat(farEndNE.latitude),
      ]);
      const color = item.nne_id === nodeIds[0] && item.nfne_id === nodeIds[1] ? "#0069F3" : "#64AF29";
      return (
        <Polyline
          key={index}
          onClick={() => handleBandwidthClick(item.nne_id, item.fne_id)}
          material={Material.fromType("Color", { color: Color.fromCssColorString(color) })}
          width={3}
          positions={positions}
        />
      );
    });
  }, [bandwidthPorts, networkElements, nodeIds]);

  const imageryProvider = useMemo(() => new OpenStreetMapImageryProvider({ url: "https://a.tile.openstreetmap.org/" }), []);

  const ref = useRef<CesiumComponentRef<CesiumViewer>>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setMapLoading(true);
    }, 2500);
    if (ref.current?.cesiumElement) {
      const layers = ref.current?.cesiumElement.imageryLayers;
      const baseLayer = layers.get(0);
      layers.remove(baseLayer);
      layers.addImageryProvider(new OpenStreetMapImageryProvider({ url: "https://a.tile.openstreetmap.org/" }));
    }
    return () => clearTimeout(timer);
  }, [bandwidthPorts]);

  useEffect(() => {
    if (ref.current?.cesiumElement) {
      ref.current?.cesiumElement.scene.requestRender();
    }
  }, [hoveredNEs]);

  useEffect(() => {
    if (ref.current?.cesiumElement) {
      const viewer = ref.current?.cesiumElement;
      viewer.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      viewer.screenSpaceEventHandler.setInputAction((movement) => {
        // @ts-ignore
        try {
          const pickedObject = viewer.scene.pick(movement.endPosition);
          if (pickedObject && defined(pickedObject)) {
            // @ts-ignore
            if (viewer._container) {
              // @ts-ignore
              viewer._container.style.cursor = "pointer";
            } // @ts-ignore
          } else if (viewer._container) {
            // @ts-ignore
            viewer._container.style.cursor = "move";
            setHoveredNEs({ ne: 0, fe: 0 });
          }
        } catch {
          // do nothing
        }
      }, ScreenSpaceEventType.MOUSE_MOVE);
    }
    return () => {
      if (ref.current?.cesiumElement) {
        ref.current?.cesiumElement.screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
      }
    };
  }, [ref.current?.cesiumElement]);

  return (
    <>
      <Viewer
        ref={ref}
        sceneMode={display === "ground" ? SceneMode.SCENE3D : SceneMode.SCENE2D}
        requestRenderMode
        className="map-container"
        style={{ visibility: !mapLoading ? "hidden" : "visible" }}
        animation={false}
        baseLayerPicker={false}
        geocoder={false}
        selectionIndicator={false}
        timeline={false}
        fullscreenButton={false}
        infoBox={false}
        navigationInstructionsInitiallyVisible={false}
      >
        <ImageryLayer imageryProvider={imageryProvider} />
        <PolylineCollection>
          {bandwidthLines}
          <PolylineGraphics arcType={0} />
        </PolylineCollection>
        {entities}
        <CameraFlyTo 
          duration={5} 
          destination={Cartesian3.fromDegrees(
            geographicCenter?.longitude, 
            geographicCenter?.latitude, 
            MAP_ZOOM.maxScale * geographicCenter.zoomFactor
          )} 
          once 
        />
          {/* <ScreenSpaceCameraController minimumZoomDistance={MAP_ZOOM.minScale} maximumZoomDistance={MAP_ZOOM.maxScale} />
          <ScreenSpaceEventHandler /> */}
      </Viewer>
    </>
  );
};  

export default MapView;
